/**
 * ASCII Art Generator - 字符集定义
 *
 * 包含所有渲染风格使用的字符集
 */

import type { CharsetType, BrailleVariant, TerminalCharset } from '../types'

// ============================================
// Classic ASCII 字符集
// ============================================

/** 标准字符集 - 从浅到深 */
export const CHARSET_STANDARD = ' .:-=+*#%@'

/** 方块字符集 - Unicode方块字符 */
export const CHARSET_BLOCKS = ' ░▒▓█'

/** 详细字符集 - 更多灰度级别 */
export const CHARSET_DETAILED = " .'`^\",:;Il!i><~+_-?][}{1)(|\\/tfjrxnuvczXYUJCLQ0OZmwqpdbkhao*#MW&8%B@$"

/** 极简字符集 */
export const CHARSET_MINIMAL = ' ·░█'

/** 二进制字符集 */
export const CHARSET_BINARY = ' 01'

/** 字母表 - 大写 */
export const CHARSET_LETTERS_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

/** 字母表 - 小写 */
export const CHARSET_LETTERS_LOWERCASE = 'abcdefghijklmnopqrstuvwxyz'

/** 字母表 - 混合大小写 */
export const CHARSET_LETTERS_MIXED = 'AaBbCcDdEeFfGgHhIiJjKkLlMmNnOoPpQqRrSsTtUuVvWwXxYyZz'

/** 符号字符集 */
export const CHARSET_LETTERS_SYMBOLS = '@#$%&*+=-<>~'

/** 默认自定义字符集 */
export const CHARSET_CUSTOM_DEFAULT = ' .:-=+*#%@'

/** 字符集映射表 */
export const CHARSETS: Record<CharsetType, string> = {
  'standard': CHARSET_STANDARD,
  'blocks': CHARSET_BLOCKS,
  'detailed': CHARSET_DETAILED,
  'minimal': CHARSET_MINIMAL,
  'binary': CHARSET_BINARY,
  'custom': CHARSET_CUSTOM_DEFAULT,
  'letters-alphabet': CHARSET_LETTERS_ALPHABET,
  'letters-lowercase': CHARSET_LETTERS_LOWERCASE,
  'letters-mixed': CHARSET_LETTERS_MIXED,
  'letters-symbols': CHARSET_LETTERS_SYMBOLS,
  'braille': '', // Braille使用单独的字符集
}

// ============================================
// Braille 盲文字符集
// ============================================

/** 完整Braille字符集 (U+2800 - U+28FF) */
export const BRAILLE_STANDARD = generateBrailleCharset()

/** 稀疏Braille字符集 */
export const BRAILLE_SPARSE = ' ⠁⠂⠄⠈⠐⠠⡀⢀⣀⣿'

/** 密集Braille字符集 */
export const BRAILLE_DENSE = ' ⠃⠇⠏⠟⠿'

/** Braille字符集映射 */
export const BRAILLE_CHARSETS: Record<BrailleVariant, string> = {
  'standard': BRAILLE_STANDARD,
  'sparse': BRAILLE_SPARSE,
  'dense': BRAILLE_DENSE,
}

/**
 * 生成完整Braille字符集
 * Braille字符从 U+2800 开始，每个字符代表一个点阵组合
 */
function generateBrailleCharset(): string {
  const chars: string[] = []
  // Braille基础字符 U+2800 (空) 到 U+28FF (全点)
  for (let i = 0x2800; i <= 0x28FF; i++) {
    chars.push(String.fromCharCode(i))
  }
  return chars.join('')
}

/**
 * 获取Braille字符 - 根据亮度值
 * @param brightness 0-255 亮度值
 * @param variant Braille变体
 */
export function getBrailleChar(brightness: number, variant: BrailleVariant = 'standard'): string {
  const charset = BRAILLE_CHARSETS[variant]
  const index = Math.floor((brightness / 255) * (charset.length - 1))
  return charset[Math.min(index, charset.length - 1)]
}

// ============================================
// Terminal 字符集
// ============================================

/** Terminal字符集映射 */
export const TERMINAL_CHARSETS: Record<TerminalCharset, string> = {
  'binary': ' 010101',
  'brackets': ' []/\\<>',
  'symbols': ' $_+',
  'mixed': ' 01[]/\\<>$_+|',
  'matrix': ' 01{}[]/\\<>|_+-',
}

// ============================================
// 粒子字符
// ============================================

/** 可用的粒子字符 */
export const PARTICLE_CHARS = ['*', '.', '+', 'x', '#', '@', ':']

/** 默认粒子字符 */
export const PARTICLE_CHAR_DEFAULT = '*'

// ============================================
// 辅助函数
// ============================================

/**
 * 根据亮度值获取对应字符
 *
 * ASCII艺术约定：亮度高的像素使用稀疏字符（保持背景色），亮度低的像素使用密集字符
 *
 * @param brightness 0-255 亮度值（高亮度 = 白色像素 = 稀疏字符，低亮度 = 黑色像素 = 密集字符）
 * @param charset 字符集（从稀疏到密集排列，第一个字符用于亮区域，最后一个用于暗区域）
 */
export function getCharByBrightness(brightness: number, charset: string): string {
  if (!charset || charset.length === 0) return ' '
  const normalizedBrightness = Math.max(0, Math.min(255, brightness))
  // 反转映射：高亮度 → 稀疏字符（索引小），低亮度 → 密集字符（索引大）
  const index = Math.floor((1 - normalizedBrightness / 255) * (charset.length - 1))
  return charset[Math.min(index, charset.length - 1)]
}

/**
 * 获取字符集
 * @param charsetType 字符集类型
 * @param customCharset 自定义字符集（当类型为custom时使用）
 */
export function getCharset(charsetType: CharsetType, customCharset?: string): string {
  if (charsetType === 'custom' && customCharset) {
    return customCharset
  }
  return CHARSETS[charsetType] || CHARSET_STANDARD
}

/**
 * 获取字符集长度（用于计算灰度级别）
 */
export function getCharsetLength(charset: string): number {
  return charset.length
}

/**
 * 验证字符集是否有效
 */
export function isValidCharset(charset: string): boolean {
  return typeof charset === 'string' && charset.length > 0
}

// ============================================
// 字符集元数据
// ============================================

/** 字符集显示名称 */
export const CHARSET_LABELS: Record<CharsetType, string> = {
  'standard': 'Standard',
  'blocks': 'Blocks',
  'detailed': 'Detailed',
  'minimal': 'Minimal',
  'binary': 'Binary',
  'custom': 'Custom',
  'letters-alphabet': 'Letters (A-Z)',
  'letters-lowercase': 'Letters (a-z)',
  'letters-mixed': 'Letters (Mixed)',
  'letters-symbols': 'Symbols',
  'braille': 'Braille',
}

/** 所有字符集类型列表 */
export const CHARSET_LIST: CharsetType[] = [
  'standard',
  'blocks',
  'detailed',
  'minimal',
  'binary',
  'custom',
  'letters-alphabet',
  'letters-lowercase',
  'letters-mixed',
  'letters-symbols',
]

/** Braille变体显示名称 */
export const BRAILLE_VARIANT_LABELS: Record<BrailleVariant, string> = {
  'standard': 'Standard',
  'sparse': 'Sparse',
  'dense': 'Dense',
}

/** Terminal字符集显示名称 */
export const TERMINAL_CHARSET_LABELS: Record<TerminalCharset, string> = {
  'binary': 'Binary',
  'brackets': 'Brackets',
  'symbols': 'Symbols',
  'mixed': 'Mixed',
  'matrix': 'Matrix',
}